import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser, loginUser } from '../../api/auth';
import { handleError } from '../../utils/errorHandling';
import InputField from '../../components/common/InputField';
import Button from '../../components/common/Button';
import useAuthStore from '../../store/authStore';
import {
  AuthPage,
  AuthCard,
  AuthCardContent,
  AuthCardHeader,
  AuthEyebrow,
  AuthTitle,
  AuthSubtitle,
  AuthForm,
  AuthCta,
  AuthWaveRow,
  SecondaryLink,
} from './AuthStyles'; // Import styled components

const Register: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await registerUser(username.trim(), email.trim(), password);
      const data = await loginUser(username.trim(), password);
      login({ username: username.trim() }, data.access);
      navigate('/chat');
    } catch (err: any) {
      handleError(err, 'Registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = Boolean(username.trim() && password.length >= 8);

  return (
    <AuthPage>
      <AuthCard>
        <AuthCardContent>
          <AuthCardHeader>
            <AuthEyebrow>Get started</AuthEyebrow>
            <AuthTitle>Create your account</AuthTitle>
            <AuthSubtitle>
              Tell us about yourself and we&apos;ll set up a workspace that keeps you focused.
            </AuthSubtitle>
          </AuthCardHeader>
          <AuthForm onSubmit={handleSubmit} noValidate>
            <InputField
              label="Username"
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder="e.g., markp"
              autoComplete="username"
              maxLength={64}
              hint="This is the name you will use to log in."
            />
            <InputField
              label="Email (optional)"
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              hint="Add an email to recover your account faster."
            />
            <InputField
              label="Password"
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Create a secure password"
              autoComplete="new-password"
              minLength={8}
              hint="Minimum 8 characters with mixed case and numbers."
            />
            <Button type="submit" disabled={isSubmitting || !isFormValid}>
              {isSubmitting ? 'Creating account…' : 'Create account'}
            </Button>
          </AuthForm>
          <AuthCta>
            <AuthWaveRow>Already have an account?</AuthWaveRow>
            <SecondaryLink to="/login">Sign in</SecondaryLink>
          </AuthCta>
        </AuthCardContent>
      </AuthCard>
    </AuthPage>
  );
};

export default Register;
